import React from "react";
import { useFetch } from "@/hooks/useApi";
import { PageHeader, Loader, Plate } from "@/components/common";
import { Card } from "@/components/ui/card";
import { gbp, num } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const Row = ({ label, value, accent, sign }) => (
  <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono font-semibold ${accent}`}>{sign}{gbp(value)}</span>
  </div>
);

export default function Profit() {
  const { data, loading } = useFetch("/profit");
  if (loading || !data) return <Loader />;
  const s = data.summary;
  const waterfall = [
    { name: "Revenue", value: s.revenue },
    { name: "Driver", value: -s.driver_costs },
    { name: "Fuel", value: -s.fuel_costs },
    { name: "Van", value: -s.van_costs },
    { name: "Profit", value: s.profit },
  ];

  return (
    <div className="fp-fade-in">
      <PageHeader title="Profit Dashboard" subtitle="Revenue − Driver − Fuel − Van − Other = Profit" testId="profit-page" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Net Profit</p><p className="font-mono text-2xl font-bold mt-2 text-primary">{gbp(s.profit)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Revenue / Stop</p><p className="font-mono text-2xl font-bold mt-2 text-emerald-400">{gbp(s.revenue_per_stop)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Cost / Stop</p><p className="font-mono text-2xl font-bold mt-2 text-rose-400">{gbp(s.cost_per_stop)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Fuel / Stop</p><p className="font-mono text-2xl font-bold mt-2 text-amber-400">{gbp(s.fuel_cost_per_stop)}</p></Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-6">
          <h3 className="font-display font-semibold mb-3">Profit Breakdown</h3>
          <Row label="Revenue" value={s.revenue} accent="text-emerald-400" sign="+" />
          <Row label="Driver costs" value={s.driver_costs} accent="text-rose-400" sign="− " />
          <Row label="Fuel costs" value={s.fuel_costs} accent="text-rose-400" sign="− " />
          <Row label="Van costs" value={s.van_costs} accent="text-rose-400" sign="− " />
          <div className="flex items-center justify-between pt-3 mt-1">
            <span className="font-semibold">Net Profit</span>
            <span className="font-mono text-2xl font-bold text-primary">{gbp(s.profit)}</span>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-semibold mb-3">Contribution</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={waterfall}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `£${v / 1000}k`} />
              <Tooltip formatter={(v) => gbp(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <Card className="p-6">
          <h3 className="font-display font-semibold mb-4">Profit per Driver</h3>
          <div className="space-y-2">
            {data.per_driver.map((d) => (
              <div key={d.driver_code} className="flex items-center justify-between text-sm">
                <span className="font-medium">{d.name} <span className="text-muted-foreground font-mono text-xs">({num(d.approved_stops)} stops)</span></span>
                <span className="font-mono font-semibold text-primary">{gbp(d.net)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-semibold mb-4">Cost per Van</h3>
          <div className="space-y-2">
            {data.per_van.map((v) => (
              <div key={v.registration} className="flex items-center justify-between text-sm">
                <Plate reg={v.registration} />
                <span className="font-mono">Fuel {gbp(v.fuel_cost)} · Van {gbp(v.van_cost)} · <span className="font-semibold text-rose-400">{gbp(v.total_cost)}</span></span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
